import React, { useState, useEffect, memo, useCallback } from 'react';
import { usePrevious } from 'site-modules/shared/hooks/use-previous';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Button from 'reactstrap/lib/Button';
import Form from 'reactstrap/lib/Form';
import Input from 'reactstrap/lib/Input';
import {
  CSAT_ANSWERS_LIMIT,
  SURVEY_CREATIVE_ID,
} from 'site-modules/shared/constants/personalized-search/personalized-search';
import { Storage } from 'site-modules/shared/utils/storage';
import { TrackingConstant } from 'client/tracking/constant';
import { EventToolbox } from 'client/utils/event-toolbox';
import { AUTOSIZED_AREA_LENGTH } from 'site-modules/shared/constants/global-search/global-search';

import './csat-search-survey.scss';

export const ANSWER = { YES: 'yes', NO: 'no' };
const STEP = { SURVEY: 'survey', TEXT: 'text', THANK_YOU: 'thank-you', CLOSED: 'closed' };
const SURVEY_BUTTONS = [
  { icon: 'icon-thumbs-up2', answer: ANSWER.YES, classes: 'me-1', ariaLabel: 'Yes, I did find what I was looking for' },
  { icon: 'icon-thumbs-down22', answer: ANSWER.NO, ariaLabel: 'No, I did not find what I was looking for' },
];
export const CSAT_PATH = 'csat-search-survey';
export const DISMISS_TRACKING = {
  [STEP.SURVEY]: 'dismiss_survey',
  [STEP.TEXT]: 'dismiss_optional_write_in',
  [STEP.THANK_YOU]: 'close_survey',
};

export const CsatSearchSurvey = memo(({ searchId }) => {
  const [storage] = useState(() => new Storage('localStorage'));
  const getHideSurvey = useCallback(id => (storage.get(CSAT_PATH) || []).includes(id), [storage]);
  const [currentStep, setCurrentStep] = useState(STEP.SURVEY);
  const [hideSurvey, setHideSurvey] = useState(false);
  const [text, setText] = useState('');
  const trimmedText = text?.trim();

  useEffect(() => {
    setHideSurvey(getHideSurvey(searchId));
  }, [getHideSurvey, searchId]);

  const prevSearchId = usePrevious(searchId);
  if (prevSearchId !== searchId) {
    setCurrentStep(STEP.SURVEY);
  }

  const updateAnsweredSearches = useCallback(() => {
    const searches = storage.get(CSAT_PATH) || [];
    const updatedSearches = [...searches, searchId];

    if (updatedSearches.length > CSAT_ANSWERS_LIMIT) {
      const numberOfItemsToRemove = updatedSearches.length - CSAT_ANSWERS_LIMIT;
      updatedSearches.splice(0, numberOfItemsToRemove);
    }

    storage.set(CSAT_PATH, updatedSearches);
  }, [searchId, storage]);

  const onThumbClick = useCallback(() => {
    setCurrentStep(STEP.TEXT);
    updateAnsweredSearches();
  }, [updateAnsweredSearches]);

  const onClose = useCallback(() => {
    setCurrentStep(STEP.CLOSED);
  }, []);

  const onDismiss = useCallback(() => {
    onClose();
    updateAnsweredSearches();
  }, [onClose, updateAnsweredSearches]);

  const onTextSubmit = useCallback(
    event => {
      event.preventDefault();
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
        event_data: {
          action_category: TrackingConstant.USER_ACTION_CATEGORY,
          action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
          action_name: TrackingConstant.ACTION_ENGAGE_SURVEY,
          subaction_name: TrackingConstant.SELECT_SURVEY,
          creative_id: SURVEY_CREATIVE_ID,
          value: `srp_search_id=${searchId}&feedback=${trimmedText}`,
        },
      });
      setCurrentStep(STEP.THANK_YOU);
    },
    [searchId, trimmedText]
  );

  const handleTextChange = useCallback(({ target: { value } }) => {
    setText(value);
  }, []);

  if (hideSurvey) return null;

  return (
    currentStep !== STEP.CLOSED && (
      <div className="base-inner-width mx-md-auto px-1 px-md-0_5 px-lg-0 my-2">
        <div
          className={classnames('csat-search-survey px-0_75 bg-blue-90 rounded-16 overlay-white-40', {
            'py-0_75': currentStep !== STEP.THANK_YOU,
            'py-1': currentStep === STEP.THANK_YOU,
          })}
          data-tracking-parent={SURVEY_CREATIVE_ID}
        >
          <div
            className={classnames('d-flex align-items-center', {
              'justify-content-between': currentStep === STEP.SURVEY,
            })}
          >
            <button
              className="close-btn background-none text-cool-gray-40 size-16 border-0 p-0 me-1 align-self-start"
              onClick={currentStep === STEP.SURVEY ? onDismiss : onClose}
              data-tracking-id={DISMISS_TRACKING[currentStep]}
              aria-label="close the survey"
            >
              <i className="icon-cross3" aria-hidden />
            </button>
            <div
              className={classnames('me-1 fw-medium', { 'fst-italic': currentStep !== STEP.TEXT })}
              aria-live="polite"
            >
              {currentStep === STEP.SURVEY && 'Did these results help you find what you needed?'}
              {currentStep === STEP.TEXT && 'Any additional feedback?'}
              {currentStep === STEP.THANK_YOU && 'Thanks for your feedback.'}
            </div>
            <div
              className={classnames({
                'd-flex': currentStep === STEP.SURVEY,
                'd-none': currentStep !== STEP.SURVEY,
              })}
            >
              {SURVEY_BUTTONS.map(({ icon, answer, classes, ariaLabel }) => (
                <button
                  key={`search-survey-${answer}`}
                  className={classnames(
                    'answer-button text-gray-darker size-16 border-0 p-0_5 d-flex justify-content-center align-items-center overlay-white-30',
                    classes
                  )}
                  onClick={onThumbClick}
                  data-tracking-id="select_survey"
                  data-tracking-value={`srp_search_id=${searchId}&feedback=${answer}`}
                  aria-label={ariaLabel}
                >
                  <i className={icon} aria-hidden />
                </button>
              ))}
            </div>
          </div>

          {currentStep === STEP.TEXT && (
            <Form
              id="csat-search-survey-form"
              noValidate
              onSubmit={onTextSubmit}
              className="d-flex align-items-center mt-0_75"
            >
              <Input
                name="query"
                autoComplete="off"
                placeholder="Type your feedback here"
                className="csat-text-input w-100 size-16 pt-0_75 ps-1 pb-0_5"
                value={text}
                onChange={handleTextChange}
                maxLength={AUTOSIZED_AREA_LENGTH}
              />
              <Button
                color="outline"
                size="sm"
                type="submit"
                className="csat-submit-button pos-a d-flex justify-content-center align-items-center bg-blue-50"
                disabled={!trimmedText}
                aria-label="Submit feedback"
              >
                <i className="icon-arrow-right8 icon text-white size-16" aria-hidden />
              </Button>
            </Form>
          )}
        </div>
      </div>
    )
  );
});

CsatSearchSurvey.displayName = 'CsatSearchSurvey';

CsatSearchSurvey.propTypes = {
  searchId: PropTypes.string.isRequired,
};
